import React, {useEffect, useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import { T } from "services/lang/LanguageService";
import { TabPanel, TabView } from "primereact/tabview";

import './ScheduledTaskHistoryTable.scss';
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {addToDate, formatTime} from "utils/time-utils";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import Editor from "@monaco-editor/react";
import {useGetAirflowTaskHistories} from "api/api-hooks/airflow-hooks";
import {ScheduledTaskDTO} from "api/model/airflow-task/ScheduledTaskDTO";
import {IScheduledTaskHistoryDTO} from "api/ScheduledTasksApi";
import JSONObjectComparer from "../../json-object-comparer/JSONObjectComparer";
import UserAvatar from "../../user-avatar/UserAvatar";
import CalendarWrapper from "../../calendar-wrapper/CalendarWrapper";


interface IScheduledTaskHistoryTableChangeParams {
    value: IScheduledTaskHistoryDTO,
}

interface IScheduledTaskHistoryTableProps {
    currentConfig: string
    dagId: string,
    taskId: string,
    // History
    value?: IScheduledTaskHistoryDTO,
    onChange?: (e: IScheduledTaskHistoryTableChangeParams) => void;
}

function ScheduledTaskHistoryTable(props: IScheduledTaskHistoryTableProps) {
    const [filteredData, setFilteredData] = useState<IScheduledTaskHistoryDTO[] | undefined>(undefined);
    const [from, setFrom] = useState<Date | undefined>(undefined)
    const [to, setTo] = useState<Date | undefined>(undefined)

    const [diffHeader, setDiffHeader] = useState<string>("")
    const [newConfig, setNewConfig] = useState<string>("")
    const [oldConfig, setOldConfig] = useState<string>("")

    const [configDialog, setConfigDialog] = useState<boolean>(false);
    const [diffDialog, setDiffDialog] = useState<boolean>(false);

    const [selectedConfig, setSelectedConfig] = useState(null);
    const [tabIndex, setTabIndex] = useState<number>(0);

    const query = useGetAirflowTaskHistories(props.dagId, props.taskId);

    useEffect(() => {
        if (selectedConfig) {
            props.onChange?.({value: selectedConfig})
        }
    }, [selectedConfig])

    const filterDateObject = () => {
        return <div className="calendars-wrapper">
            <div>
                <div className="bigger-font">{T("word.from")}</div>
                <CalendarWrapper inline value={from} onChange={(e) => setFrom(e)}/>
            </div>
            <div>
                <div className="bigger-font">{T("word.to")}</div>
                <CalendarWrapper inline value={to} onChange={(e) => setTo(e)}/>
            </div>
        </div>
    }

    const customDateFilter = () => {
        let fromDate = new Date(0);
        let toDate = new Date()

        if (from) {
            fromDate = from;
        }
        if (to) {
            toDate = to;
        } else {
            toDate.setHours(0);
            toDate.setMinutes(0.);
            toDate.setSeconds(0);
            toDate.setMilliseconds(0);
        }
        if (fromDate > toDate) {
            const tempDate = fromDate;
            fromDate = new Date(toDate)
            toDate = new Date(tempDate)
        }

        toDate = addToDate(toDate, 1, "DAYS")

        if (query.data) {
            setFilteredData(query.data.filter(ob => new Date(ob.changeOn) >= fromDate && new Date(ob.changeOn) <= toDate))
        }
    }

    const clearFilter = () => {
        setFilteredData(undefined);
        setFrom(undefined)
        setTo(undefined);
    }

    const configBody = (dataRow: any, {rowIndex}: any) => {
        const setConfig = (dataRow: any) => {
            setDiffHeader(dataRow.changeOn)
            setNewConfig(dataRow.config)
            setOldConfig(query.data ? query.data[rowIndex + 1].config : "")
        }
        const setCompareToCurrentCode = (dataRow: any) => {
            setDiffHeader(T("label.compare-to-current-code"))
            setNewConfig(props.currentConfig)
            setOldConfig(dataRow.config)
        }

        return <div className="dialog-button-wrapper">
            <Button icon="pi pi-cog"
                    tooltip={T("word.config")}
                    className="p-button-sm"
                    onClick={() => {
                        setConfigDialog(true);
                        setConfig(dataRow)
                    }}/>
            <Button icon="pi pi-clock"
                    tooltip={T("label.compare-to-current-config")}
                    className="p-button-sm"
                    onClick={() => {
                        setDiffDialog(true);
                        setCompareToCurrentCode(dataRow)
                    }}/>

            {/* cant make only one if statement. 2 is necessary */}
            {filteredData ?
                filteredData.length - 1 !== rowIndex &&
                <Button icon="pi pi-history"
                        className="p-button-sm"
                        tooltip={T("label.compare-to-previous-config")}
                        onClick={() => {
                            setDiffDialog(true);
                            setConfig(dataRow)
                        }}/>
                :
                (query.data && query.data.length - 1 !== rowIndex) &&
                <Button icon="pi pi-history"
                        className="p-button-sm"
                        tooltip={T("label.compare-to-previous-config")}
                        onClick={() => {
                            setDiffDialog(true);
                            setConfig(dataRow)
                        }}/>
            }
        </div>
    }

    const changedByBody = (rowData: any) => {
        return <UserAvatar username={rowData.changeBy} />
    }

    let header = "";
    if (diffHeader) {
      if (diffHeader !== T("label.compare-to-current-code")) {
        header = formatTime(diffHeader);
      } else {
        header = diffHeader;
      }
    }
    const decodeBase64Config = (base64: string) => {
        try {
            if (!base64) return T("message.there-is-no-data-to-decode");
            const decodedString = atob(base64);
            if (decodedString.charCodeAt(0) === 0x50 && decodedString.charCodeAt(1) === 0x4B) {
                return T("message.zip-file-detected");            }
    
            return decodedString;
        } catch (error) {
            easasAlert({
                summary: T("error.failed-to-decode-data"),
                severity: "error",
            });
            return T("error.invalid-base64-data");
        }
    };
        
    return <>
        {query.error &&
            <span>{T("error.task-history-template-selection-error")}</span>
        }

        {query.isLoading && !query.error &&
            <span>{T("message.task-history-templates-is-loading")}</span>
        }

        {query.isSuccess &&
            <DataTable value={filteredData || query.data}
                       size="small"
                       responsiveLayout="scroll"
                       paginator
                       paginatorTemplate="CurrentPageReport FirstPageLink PageLinks LastPageLink RowsPerPageDropdown"
                       currentPageReportTemplate="{first} - {last} / {totalRecords}" rows={10}
                       selectionMode="single"
                       selection={selectedConfig} onSelectionChange={e => setSelectedConfig(e.value)} dataKey="changeOn"
            >
                <Column selectionMode="single" headerStyle={{width: '0'}}/>
                <Column field="changeOn" header={T("word.date")}
                        filter
                        filterElement={filterDateObject}
                        showFilterMatchModes={false} showFilterMenuOptions={false}
                        body={(data) => formatTime(data.changeOn)}
                        onFilterApplyClick={() => customDateFilter()}
                        onFilterClear={() => clearFilter()}
                        style={{whiteSpace: "nowrap", flexWrap: "nowrap"}}
                />
                <Column field="changeBy" header={T("label.changed-by")}
                        body={changedByBody}
                        style={{width: "0", whiteSpace: "nowrap", flexWrap: "nowrap"}}/>
                <Column body={configBody} style={{width: "0", whiteSpace: "nowrap"}}/>
            </DataTable>
        }

        <Dialog header={T("word.config")}
                onHide={() => setConfigDialog(false)}
                visible={configDialog}
                blockScroll>
            {newConfig &&
                <TabView activeIndex={tabIndex} onTabChange={(e) => setTabIndex(e.index)}>
                    <TabPanel header={T("word.config")}>
                        <Editor height="100vh"
                            width="60vw"
                            defaultLanguage="json"
                            value={JSON.stringify(new ScheduledTaskDTO(JSON.parse(newConfig)), null, 4)}
                            options={{readOnly: true}}
                        />
                    </TabPanel>
                    <TabPanel header={T("action.read-code")}>
                    <Editor height="100vh"
                            width="60vw"
                            defaultLanguage="json"
                            value={decodeBase64Config(JSON.parse(newConfig).codeB64)}
                            options={{readOnly: true}}
                        />
                    </TabPanel>
                </TabView>                
            }
        </Dialog>

        <Dialog onHide={() => setDiffDialog(false)}
                visible={diffDialog}
                blockScroll
                header={header}
                style={{width: "min(100vw, 1000px)", height: "90vh"}}
        >
            {newConfig && oldConfig &&
                <JSONObjectComparer
                    oldValue={JSON.stringify(new ScheduledTaskDTO(JSON.parse(oldConfig)), null, 4)}
                    newValue={JSON.stringify(new ScheduledTaskDTO(JSON.parse(newConfig)), null, 4)}
                    rightTitle={diffHeader === T("label.compare-to-current-code") ? T("label.current-code") : undefined}
                />
            }
        </Dialog>
    </>
}

export default ScheduledTaskHistoryTable